// PaywallBanner — v0.8.0 · BL-040 (trial credits)
//
// Dismissible banner shown across protected pages when the trial is running
// low on EITHER dimension of the "7 days OR 5 analyses" trial:
//   • days remaining (≤ 3 days left) — original behaviour, or
//   • analyses remaining (≤ 2 left), or
//   • analyses EXHAUSTED (0 left) — a distinct, stronger upgrade prompt that
//     shows even when trial days remain (the analyzer is credit-blocked).
// Prompts the user to upgrade before access is cut off.
//
// Hides automatically when:
//   • User is a paid subscriber (isPro)
//   • Trial has expired (PaywallGate handles that full-page block)
//   • Trial has plenty of both days and analyses left
//   • User dismisses the banner for this session
//
// Credit fields are advisory (from SubscriptionContext); the backend remains
// the security boundary (enforced at /api/analyze-property).

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSubscription } from '../context/SubscriptionContext';
import { recordUpgradeIntent } from '../lib/upgradeIntent';

const SHOW_THRESHOLD_DAYS = 3;

export default function PaywallBanner() {
  const {
    isTrialing,
    isPro,
    isExpired,
    trialDaysLeft,
    tier,
    analysisCreditsRemaining,
    analysisCreditsExhausted,
    analysisCreditsLow,
  } = useSubscription();
  const [dismissed, setDismissed] = useState(false);
  const navigate = useNavigate();

  const lowDays = trialDaysLeft <= SHOW_THRESHOLD_DAYS;

  // Show only for a trialing, non-dismissed user when at least one dimension
  // is running low (or analyses are exhausted).
  if (
    isPro ||
    isExpired ||
    !isTrialing ||
    dismissed ||
    !(lowDays || analysisCreditsLow || analysisCreditsExhausted)
  ) {
    return null;
  }

  // Choose the most urgent message: exhaustion > low credits > low days.
  let message;
  if (analysisCreditsExhausted) {
    message = (
      <>
        🚫 You've <strong>used all your trial analyses</strong>.
        Upgrade to Investor to keep analyzing.
      </>
    );
  } else if (analysisCreditsLow) {
    const n = analysisCreditsRemaining;
    const analysisLabel = n === 1 ? '1 analysis left' : `${n} analyses left`;
    message = (
      <>
        ⏳ <strong>{analysisLabel}</strong> in your free trial.
        Upgrade to Investor to keep full access.
      </>
    );
  } else {
    const dayLabel = trialDaysLeft === 1 ? '1 day' : `${trialDaysLeft} days`;
    message = (
      <>
        ⏳ <strong>{dayLabel} left</strong> in your free trial.
        Upgrade to Investor to keep full access.
      </>
    );
  }

  return (
    <div style={bannerStyle}>
      <span style={messageStyle}>{message}</span>
      <div style={actionsStyle}>
        <button
          style={upgradeButtonStyle}
          onClick={() => {
            // BL-038 Story 38.5 — capture the upgrade intent (non-billing
            // demand signal), then route to the plan-selection surface instead
            // of dead-ending. No checkout is triggered under lockdown.
            recordUpgradeIntent({ from: 'paywall_banner', currentTier: tier });
            navigate('/pricing');
          }}
        >
          Upgrade to Investor
        </button>
        <button
          style={dismissButtonStyle}
          onClick={() => setDismissed(true)}
          aria-label="Dismiss banner"
        >
          ✕
        </button>
      </div>
    </div>
  );
}

const bannerStyle = {
  background: 'linear-gradient(90deg, #1e40af 0%, #2563eb 100%)',
  color: '#ffffff',
  padding: '12px 20px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '16px',
  flexWrap: 'wrap',
  fontSize: '14px',
};

const messageStyle = {
  flex: 1,
  lineHeight: '1.4',
};

const actionsStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
};

const upgradeButtonStyle = {
  background: '#ffffff',
  color: '#1e40af',
  border: 'none',
  borderRadius: '8px',
  padding: '6px 16px',
  fontWeight: 700,
  fontSize: '13px',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};

const dismissButtonStyle = {
  background: 'transparent',
  border: 'none',
  color: 'rgba(255,255,255,0.7)',
  fontSize: '16px',
  cursor: 'pointer',
  padding: '4px 6px',
  lineHeight: 1,
};
